import React, { useState, useRef, useEffect } from 'react'
import { useViewStore } from '../Contexts/ViewContext'
import { useRetorikStore } from '../Contexts/RetorikContext'
import { useLocaleStore } from '../Contexts/LocaleContext'
import { useUtilsStore } from '../Contexts/UtilsContext'
import { useSpeechStore } from '../Contexts/SpeechContext'
import { useDirectlineStore } from '../Contexts/DirectlineContext'

import { SendMessageIcon, SendMessageIconReversed } from '../Icons/Miscellaneous'
import { MicrophoneIcon } from '../Icons/MenuIcons'
import { Mode } from '../../models/enums'

interface SendTextProps {
  className?: string
}

const SendTextBox = ({ className }: SendTextProps): JSX.Element => {
  const isRTL = useLocaleStore((state) => state.isRTL)
  const translation = useLocaleStore((state) => state.currentTranslations)
  const disabledSpeechMode = useRetorikStore((state) => state.configuration.disableSpeechMode)
  const canFocusSendBox = useRetorikStore((state) => state.canFocusSendBox)
  const preventVirtualKeyboard = useRetorikStore((state) => state.configuration.preventVirtualKeyboard)
  const sendBoxPlaceHolder = useRetorikStore((state) => state.sendBoxPlaceHolder)
  const useOldRemote = useRetorikStore((state) => state.configuration.useOldRemote)
  const isMobile = useViewStore((state) => state.isMobile)
  const setUserTyping = useUtilsStore((state) => state.actions.setUserTyping)
  const { setSendBoxRef, setFromTextToVocal } = useRetorikStore((state) => state.actions)
  const setMode = useSpeechStore((state) => state.actions.setMode)
  const sendMessage = useDirectlineStore((state) => state.actions.sendMessage)

  const [height, setHeight] = useState<string | undefined>()
  const [message, setMessage] = useState<string>('')
  const timerRef = useRef<NodeJS.Timer | null>(null)

  const textareaRef: React.MutableRefObject<HTMLTextAreaElement | null> = useRef(null)

  useEffect(() => {
    if (canFocusSendBox && textareaRef.current && document.activeElement !== textareaRef.current) {
      textareaRef.current.focus()
    }
  }, [textareaRef, canFocusSendBox])

  useEffect(() => {
    if (textareaRef.current) {
      setSendBoxRef(textareaRef.current)
      // Add focusin event to open virtual keyboard if it isn't disabled
      !preventVirtualKeyboard && textareaRef.current.addEventListener('focusin', () => navigator.virtualKeyboard?.show())
      textareaRef.current.addEventListener('focusout', () => setUserTyping(false))
    }

    return () => {
      setUserTyping(false)
      timerRef?.current && clearTimeout(timerRef.current)
    }
  }, [preventVirtualKeyboard, textareaRef])

  const handleMessageChange = (): void => {
    timerRef?.current && clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      setUserTyping(false)
    }, 1000)
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setUserTyping(true)
    handleMessageChange()
    setMessage(e.currentTarget.value)
    textareaRef?.current && setHeight(`${textareaRef.current.scrollHeight}px`)
  }

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === 'NumpadEnter') {
      e.preventDefault()
      e.stopPropagation()
      handleSend()
    }
  }

  const handleSend = (): void => {
    if (message?.length) {
      sendMessage(message)
      setMessage('')
      setHeight(undefined)
      timerRef?.current && clearTimeout(timerRef.current)
      setUserTyping(false)
    }
  }

  const handleClick = (): void => {
    setFromTextToVocal(true)
    setMode(Mode.vocal)
  }

  return (
    <div
      id='retorik-send-textbox'
      dir={isRTL ? 'rtl' : 'ltr'}
      className={`rf-relative rf-min-h-12 rf-px-4 rf-text-base rf-pt-3 rf-pb-3 rf-flex rf-items-center rf-border rf-border-textBoxBorder rf-rounded-8 rf-bg-truewhite rf-shadow-[inset_0_0_6px_#0000003B] ${className}`}
    >
      <textarea
        ref={textareaRef}
        // @ts-ignore
        virtualkeyboardpolicy='manual'
        className={`rf-input-hidden rf-outline-none rf-max-h-full rf-overflow-y-clip rf-bg-truewhite rf-w-[calc(100%-4rem)] rf-resize-none rf-scrollbar-hidden ${isMobile && 'placeholder:rf-text-black placeholder:rf-opacity-100'}`}
        value={message}
        placeholder={sendBoxPlaceHolder || translation.menu.message}
        onKeyDown={(e): void => handleKeyDown(e)}
        onChange={(e): void => handleChange(e)}
        style={{
          height: height || '1.5rem'
        }}
      ></textarea>

      {/* Toggle to vocal mode button */}
      {!isMobile && !disabledSpeechMode && !useOldRemote && (
        <button className={`rf-absolute ${isRTL ? 'rf-left-12' : 'rf-right-12'} rf-h-full rf-w-8 rf-flex rf-justify-center rf-items-center`} onClick={handleClick}>
          <MicrophoneIcon className='rf-h-4 large-vertical:rf-h-5' color={'#00000080'} />
        </button>
      )}

      {/* Sending button */}
      <button
        className={`rf-absolute ${
          isRTL ? 'rf-left-2' : 'rf-right-2'
        } rf-h-8 rf-w-8 rf-pt-1 rf-pr-1 rf-flex rf-justify-center rf-items-center rf-border rf-border-secondary rf-bg-secondary rf-rounded-half`}
        onClick={handleSend}
      >
        {isRTL ? <SendMessageIconReversed className='rf-w-5' color='#FFF' /> : <SendMessageIcon className='rf-w-5' color='#FFF' />}
      </button>
    </div>
  )
}

export default SendTextBox
